import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'

type CnpjIePriceLearnMoreProps = {
  className?: string
  value: string
  triggerClassName?: string
}

export function CnpjIePriceLearnMore({
  className,
  value,
  triggerClassName,
}: CnpjIePriceLearnMoreProps) {
  const { t } = useTranslation()
  const priceToken = '__PRICE__'
  const text = t('product.cnpj_ie_potential_price_prefix', { value: priceToken })
  const [beforeValue = '', afterValue = ''] = text.split(priceToken)

  return (
    <Dialog>
      <span className={cn('font-normal text-muted-foreground', className)}>
        {beforeValue}
        <span className="font-semibold">{value}</span>
        {afterValue}
        {' '}
        <DialogTrigger asChild>
          <button
            type="button"
            className={cn(
              'inline cursor-pointer border-0 bg-transparent p-0 font-normal text-inherit underline underline-offset-2 transition-opacity hover:opacity-80',
              triggerClassName,
            )}
          >
            {t('product.cnpj_ie_learn_more')}
          </button>
        </DialogTrigger>
      </span>

      <DialogContent className="w-[calc(100vw-1rem)] max-w-[38rem] max-h-[calc(100dvh-1rem)] overflow-hidden rounded-[22px] border-zinc-200 bg-white p-0 shadow-[0_32px_80px_-48px_rgba(15,23,42,0.34)] sm:max-h-[calc(100dvh-3rem)] [&_[data-slot=dialog-close]]:top-4 [&_[data-slot=dialog-close]]:right-4 [&_[data-slot=dialog-close]]:rounded-full [&_[data-slot=dialog-close]]:border [&_[data-slot=dialog-close]]:border-zinc-200 [&_[data-slot=dialog-close]]:bg-white [&_[data-slot=dialog-close]]:p-1.5 [&_[data-slot=dialog-close]]:opacity-100 [&_[data-slot=dialog-close]]:shadow-[0_8px_18px_-14px_rgba(15,23,42,0.35)]">
        <div className="max-h-[calc(100dvh-1rem)] overflow-y-auto overscroll-contain sm:max-h-[calc(100dvh-3rem)]">
          <div className="border-b border-zinc-100 px-5 py-5 sm:px-6 sm:py-6">
            <DialogHeader className="space-y-2.5 text-left">
              <div className="inline-flex w-fit rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-700">
                {t('product.cnpj_ie_modal_badge')}
              </div>
              <DialogTitle className="max-w-[30rem] pr-10 text-[1.25rem] leading-tight font-semibold tracking-tight text-zinc-950 sm:text-[1.4rem]">
                {t('product.cnpj_ie_modal_title')}
              </DialogTitle>
              <DialogDescription className="max-w-[31rem] text-sm leading-6 text-zinc-600">
                {t('product.cnpj_ie_modal_description')}
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="space-y-4 px-5 py-5 sm:px-6 sm:py-6">
            <section className="rounded-[18px] border border-amber-200/80 bg-amber-50/60 p-4">
              <h3 className="text-sm font-semibold text-zinc-950">
                {t('product.cnpj_ie_modal_missing_ie_title')}
              </h3>
              <p className="mt-2 text-sm leading-6 text-zinc-600">
                {t('product.cnpj_ie_modal_missing_ie_text_1')}
              </p>
              <p className="mt-2 text-sm leading-6 text-zinc-600">
                {t('product.cnpj_ie_modal_missing_ie_text_2')}
              </p>
            </section>

            <section className="rounded-[18px] border border-zinc-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-zinc-950">
                {t('product.cnpj_ie_modal_how_to_title')}
              </h3>
              <p className="mt-2 text-sm leading-6 text-zinc-600">
                {t('product.cnpj_ie_modal_how_to_text_1')}
              </p>
              <p className="mt-2 text-sm leading-6 text-zinc-600">
                {t('product.cnpj_ie_modal_how_to_text_2')}
              </p>
            </section>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
